import React, { Suspense, useRef } from "react";

import AdminHoverBox from "@/shared/components/dev/AdminHoverBox";
import Button from "@/dashboard/core/components/Button";
import EnvironmentIndicator from "@/shared/components/dev/EnvironmentIndicator";
import GlobalPrefetcher from "@/dashboard/shared/components/Layout/GlobalPrefetcher";
import { Helmet } from "react-helmet";
import Logo from "@/public/core/components/Logo";
import Notifications from "@/dashboard/shared/components/Notifications";
import { Outlet } from "react-router-dom";
import Sidebar from "@/dashboard/shared/components/Sidebar/Sidebar";
import log from "loglevel";
import styled from "styled-components";
import theme from "@/styles/theme";
import { useDetailSideSheet } from "@/dashboard/shared/context/DetailSideSheetContext";
import { useRightSidebar } from "@/dashboard/shared/context/RightSidebarContext";

const DesktopContainer = styled.div`
  box-sizing: border-box;

  & *,
  & *:before,
  & *:after {
    box-sizing: inherit;
  }

  min-height: 100vh;
  display: flex;
  color: ${theme.colors.gray900};

  ${theme.media.phone`
    display: none;
  `}
`;

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;

  ${(props) =>
    props.$isVisible
      ? `
        opacity: 1;
        visibility: visible;
      `
      : `
        opacity: 0;
        visibility: hidden;
      `}
  background-color: rgba(44, 44, 44, 0.5);

  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const MobileContainer = styled.div`
  display: none;

  ${theme.media.phone`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
    height: 100vh;
  `}
`;

const MobileContent = styled.div`
  width: 241px;
`;

const MobileWarning = styled.div`
  padding: ${theme.spacing.unit * 3}px 0;

  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
`;

const LeftColumn = styled.div`
  width: 216px;
  position: sticky;
  left: 0;
  top: 0;
  background-color: ${theme.colors.white};
  border-right: 1px solid ${theme.colors.gray50};
`;

const RightColumn = styled.div`
  height: 100vh;
  overflow-y: auto;
  // scrollbar-gutter: stable both-edges;
  width: 100%;
`;

const RightColumnScrollContainer = styled.div`
  height: 100%;
`;

const RightColumnContent = styled.div`
  position: relative;

  margin-left: auto;
  margin-right: auto;

  background-color: ${theme.colors.white};
  padding: 0 ${theme.spacing.unit * 2}px ${theme.spacing.unit * 2}px;

  display: flex;
  flex-direction: column;

  max-width: 1504px;
  box-sizing: border-box;

  height: 100%;
`;

const RightSidebar = styled.div`
  background-color: ${theme.colors.white};
  height: 100%;
  width: 360px;
  border-left: 1px solid ${theme.colors.gray100};
  right: ${(props) => (props.$isVisible ? 0 : "-361px")};
  position: fixed;
  transition: right 0.3s ease-in-out;
`;

const RightSidebarPlaceholder = styled.div`
  width: ${(props) => (props.$isVisible ? "360px" : "0")};
  // min-width: ${(props) => (props.$isVisible ? "320px" : "0")};
  transition: width 0.3s ease-in-out;
  flex-shrink: 0;
`;

const DetailSideSheet = styled.div`
  ${(props) => props.$shouldRaiseAboveOverlay && "z-index: 10000;"}

  background-color: ${theme.colors.white};
  height: 100%;
  width: ${(props) => props.$width || 400}px;
  border-left: 1px solid ${theme.colors.gray50};
  right: ${(props) =>
    props.$isVisible ? 0 : `-${(props.$width ? props.$width : 400) + 1}px`};
  // padding: 0 ${theme.spacing.unit * 2}px;

  position: fixed;

  // flex-shrink: 0; /* Prevent sidebar from shrinking */
  // transition: transform 0.3s; /* Smooth transition for sidebar animation */
  // transform: translateX(
  //   ${(props) => (props.$isVisible ? 0 : "-320px")}
  ); /* Move sidebar off the screen by default */

  transition: right 0.3s ease-in-out;

  /* Modal */
  box-shadow: 0px 20px 40px 0px rgba(0, 0, 0, 0.10);
`;

const LayoutWithSideSheet = () => {
  const { rightSidebarContent, isRightSidebarOpen } = useRightSidebar();
  const {
    detailContent,
    isDetailSideSheetOpen,
    sideSheetWidth,
    shouldDarkenWithDetail,
  } = useDetailSideSheet();

  // Create ref for the scrollable container
  const layoutScrollableElementRef = useRef(null);

  log.debug("The sideSheetWidth: ", sideSheetWidth);

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <style type="text/css">{`
          body {
              background: ${theme.colors.white};
          }

          #root {
            height: 100vh;
          }
      `}</style>
      </Helmet>
      <AdminHoverBox />
      <EnvironmentIndicator />
      <Notifications />
      <DesktopContainer>
        <OverlayContainer
          $isVisible={shouldDarkenWithDetail && isDetailSideSheetOpen}
        />
        <LeftColumn>
          <Sidebar />
        </LeftColumn>
        <RightColumn ref={layoutScrollableElementRef}>
          <RightColumnScrollContainer>
            <RightColumnContent>
              <Suspense fallback={null}>
                <Outlet context={{ layoutScrollableElementRef }} />
              </Suspense>
            </RightColumnContent>
          </RightColumnScrollContainer>
        </RightColumn>
        <RightSidebar $isVisible={isRightSidebarOpen}>
          {rightSidebarContent}
        </RightSidebar>
        <RightSidebarPlaceholder $isVisible={isRightSidebarOpen} />
        <DetailSideSheet
          $isVisible={isDetailSideSheetOpen}
          $width={sideSheetWidth}
          $shouldRaiseAboveOverlay={
            shouldDarkenWithDetail && isDetailSideSheetOpen
          }
        >
          {detailContent}
        </DetailSideSheet>
      </DesktopContainer>
      <MobileContainer>
        <MobileContent>
          <Logo />
          <MobileWarning>
            To access your dashboard, please visit on a desktop or increase the
            width of your browser window
          </MobileWarning>
          <Button as="a" href="/logout" isSmall isSecondary>
            Log out
          </Button>
        </MobileContent>
      </MobileContainer>
    </>
  );
};

const Layout = () => {
  return (
    <>
      <GlobalPrefetcher />
      <LayoutWithSideSheet />
    </>
  );
};

export default Layout;
